import React, { useState } from 'react';
import {
  getCurrentPDF,
  getPresignedUploadUrl,
  uploadPDFToS3,
  confirmPDFUpload,
  deletePDF
} from '../services/pdfService';

const PDFUploadForm = ({ onSuccess, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [currentPDF, setCurrentPDF] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    loadCurrentPDF();
  }, []);

  const loadCurrentPDF = async () => {
    try {
      const data = await getCurrentPDF();
      if (data.exists) {
        setCurrentPDF(data);
      }
    } catch (err) {
      console.error('Error loading current PDF:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setError(null);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Please select a PDF file');
      setSelectedFile(null);
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(10);
      setError(null);

      // Step 1: Get presigned URL
      const presignedData = await getPresignedUploadUrl(selectedFile.name);
      setUploadProgress(30);

      // Step 2: Upload to S3
      await uploadPDFToS3(presignedData, selectedFile);
      setUploadProgress(70);

      // Step 3: Confirm upload
      await confirmPDFUpload(selectedFile.name, selectedFile.size);
      setUploadProgress(100);

      // Success
      setTimeout(() => {
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      }, 500);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload PDF. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the current PDF?')) {
      return;
    }

    try {
      setUploading(true);
      await deletePDF();
      setCurrentPDF(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.detail || 'Failed to delete PDF');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="pdf-upload-form">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="pdf-upload-form">
      {currentPDF && (
        <div className="current-pdf-info">
          <h3>Current PDF</h3>
          <p><strong>Filename:</strong> {currentPDF.filename}</p>
          <p><strong>Uploaded:</strong> {new Date(currentPDF.uploaded_at).toLocaleString()}</p>
          <p><strong>Uploaded by:</strong> {currentPDF.uploaded_by}</p>
          <button
            onClick={handleDelete}
            disabled={uploading}
            className="delete-button"
          >
            Delete Current PDF
          </button>
        </div>
      )}

      <div className="upload-section">
        <h3>{currentPDF ? 'Replace PDF' : 'Upload PDF'}</h3>
        <div className="file-input-wrapper">
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            disabled={uploading}
            id="pdf-file-input"
            className="file-input"
          />
          <label htmlFor="pdf-file-input" className="file-input-label">
            {selectedFile ? selectedFile.name : 'Choose PDF file'}
          </label>
        </div>

        {selectedFile && (
          <div className="file-info">
            <p>Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p>{uploadProgress}% complete</p>
          </div>
        )}

        <div className="form-actions">
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="upload-button"
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
          <button
            onClick={onClose}
            disabled={uploading}
            className="cancel-button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFUploadForm;
